import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { computeFlyoutPosition } from './submenu-flyout-position';

/**
 * Lifts a third-level submenu out of the second-level dropdown so it can escape
 * that dropdown's scrolling.
 *
 * The second-level dropdown caps its height and scrolls (Admin carries 25
 * children, taller than a laptop viewport). A scroll container clips its
 * absolutely positioned descendants on both axes - once `overflow-y` is not
 * `visible`, `overflow-x: visible` computes to `auto` - so the third-level
 * panels, which the stylesheet opens at `left: 100%`, would be clipped at the
 * dropdown's right edge. 15 of Admin's 25 children have one.
 *
 * Switching the panel to `position: fixed` takes it out of that clipping
 * context. No ancestor of the topbar sets a transform, filter or perspective,
 * so `fixed` resolves against the viewport as intended. Positioning it then
 * becomes this directive's job, which also lets the panel flip to the left of
 * its anchor near the right edge - what the stylesheet's `li.last-elements`
 * rule was meant to do before it was left dead (that class is never applied).
 *
 * Apply to a second-level `<li>` that has a `.submenu` child, and to a
 * third-level one too: once lifted, the third-level panel caps its own height
 * and scrolls (below), so it clips a fourth-level panel the same way
 * (LLS-1049 - Task Config > Checklists).
 *
 * The lifted panel overlaps its scrolling parent's right edge, where the
 * parent's scrollbar is. The browser hit-tests that scrollbar above the lifted
 * panel, so the pointer crossing to the panel would land on it and leave the
 * anchor, dropping the `:hover` that keeps the panel open, and the scrollbar's
 * own hover styling would flash on the way. While a lifted panel is open, its
 * parent's scrollbar is therefore hidden. The parent still scrolls by wheel and
 * touchpad, and keeps its width: its items widen into the scrollbar's place, so
 * the lifted panel opens flush against the parent's border.
 *
 * Desktop-only (>=1280px); below that the submenus are click-toggled
 * accordions in normal flow and need none of this.
 */
@Directive({
  selector: '[submenuFlyout]',
  standalone: false,
})
export class SubmenuFlyoutDirective implements OnInit, OnDestroy {

  private static readonly DESKTOP_BREAKPOINT = 1280;
  private static readonly NO_TRANSITION_CLASS = 'submenu-flyout-no-transition';

  /** The item whose open panel hid each parent's scrollbar; only it restores it. */
  private static readonly scrollbarHiddenBy = new WeakMap<HTMLElement, SubmenuFlyoutDirective>();

  private unlisten: (() => void)[] = [];
  private unlistenReposition: (() => void)[] = [];
  private rafId: number | null = null;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      const host = this.el.nativeElement;

      this.unlisten.push(
        this.renderer.listen(host, 'mouseenter', () => this.open()),
        this.renderer.listen(host, 'mouseleave', () => this.close()),
      );
    });
  }

  ngOnDestroy(): void {
    this.close();
    this.unlisten.forEach(fn => fn());
    this.unlisten = [];
  }

  private get isDesktop(): boolean {
    return window.innerWidth >= SubmenuFlyoutDirective.DESKTOP_BREAKPOINT;
  }

  private get flyout(): HTMLElement | null {
    return this.el.nativeElement.querySelector(':scope > .submenu');
  }

  /** The panel this item sits in, which may be the one that scrolls. */
  private get parentPanel(): HTMLElement | null {
    return this.el.nativeElement.parentElement;
  }

  private hideParentScrollbar(): void {
    const parent = this.parentPanel;
    if (!parent) {
      return;
    }
    const owners = SubmenuFlyoutDirective.scrollbarHiddenBy;
    if (owners.has(parent)) {
      owners.set(parent, this);
      return;
    }

    const style = getComputedStyle(parent);
    const scrollbar = parent.offsetWidth - parent.clientLeft - parent.clientWidth
      - (parseFloat(style.borderRightWidth) || 0);
    if (scrollbar <= 0) {
      return;
    }
    // The parent sizes to its content plus the scrollbar; pin that width so it
    // does not narrow when the scrollbar goes.
    const width = `${parent.getBoundingClientRect().width}px`;
    this.withoutTransition(parent, () => {
      this.renderer.setStyle(parent, 'boxSizing', 'border-box');
      this.renderer.setStyle(parent, 'width', width);
      this.renderer.setStyle(parent, 'scrollbarWidth', 'none');
    });
    owners.set(parent, this);
  }

  private restoreParentScrollbar(): void {
    const parent = this.parentPanel;
    if (!parent || SubmenuFlyoutDirective.scrollbarHiddenBy.get(parent) !== this) {
      return;
    }
    this.withoutTransition(parent, () => {
      ['boxSizing', 'width', 'scrollbarWidth'].forEach(prop => this.renderer.removeStyle(parent, prop));
    });
    SubmenuFlyoutDirective.scrollbarHiddenBy.delete(parent);
  }

  /**
   * Swaps the scrollbar out or back in one step. The panels inherit an
   * all-property `transition` (app.css), so their size would otherwise animate
   * visibly, and the next swap would measure a panel still on its way back.
   */
  private withoutTransition(panel: HTMLElement, change: () => void): void {
    this.renderer.addClass(panel, SubmenuFlyoutDirective.NO_TRANSITION_CLASS);
    change();
    // Commit the change while transitions are off; turning them back on after
    // that changes nothing they could animate.
    panel.getBoundingClientRect();
    this.renderer.removeClass(panel, SubmenuFlyoutDirective.NO_TRANSITION_CLASS);
  }

  private open(): void {
    if (!this.isDesktop || !this.flyout) {
      return;
    }

    // Measuring the panel forces the style recalc that applies the stylesheet's
    // :hover reveal, so it is laid out by the time it is read - and placing it
    // here, rather than in a frame, keeps it from painting unpositioned first.
    this.position();

    this.zone.runOutsideAngular(() => {
      // The panel is fixed to the viewport while its anchor still scrolls inside
      // the dropdown, so it has to be re-placed as that scrolling happens. Every
      // ancestor panel counts, not just the nearest: a fourth-level panel's anchor
      // also moves when the outer dropdown scrolls. The outer instance subscribed
      // first (it was hovered first), so its frame runs first and this one measures
      // its anchor after the parent panel has already moved.
      let scroller = this.el.nativeElement.closest('.submenu') as HTMLElement | null;
      while (scroller) {
        this.unlistenReposition.push(
          this.renderer.listen(scroller, 'scroll', () => this.scheduleReposition()),
        );
        scroller = scroller.parentElement?.closest('.submenu') as HTMLElement | null;
      }
      this.unlistenReposition.push(
        this.renderer.listen('window', 'resize', () => this.scheduleReposition()),
      );
    });
  }

  private scheduleReposition(): void {
    if (this.rafId != null) {
      return;
    }
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.position();
    });
  }

  private position(): void {
    const flyout = this.flyout;
    if (!flyout || !this.isDesktop) {
      return;
    }

    // The panel inherits an all-property `transition` (app.css). Once fixed, its
    // stylesheet `left: 100%; top: 0` resolves against the viewport, so the
    // browser would animate it in from the corner of the screen.
    this.renderer.setStyle(flyout, 'transition', 'none');
    // Lift it before measuring: still absolute, it overflows the parent panel,
    // which then shows a scrollbar for that moment and would be mistaken for a
    // real one below. `fixed` does not change the panel's width, and the
    // position is written before the next paint.
    this.renderer.setStyle(flyout, 'position', 'fixed');
    this.hideParentScrollbar();

    const anchor = this.el.nativeElement.getBoundingClientRect();
    const panel = flyout.getBoundingClientRect();

    // A panel that is still hidden measures 0x0; wait for the reveal instead of
    // placing it against that.
    if (panel.width === 0 || panel.height === 0) {
      this.scheduleReposition();
      return;
    }

    const { top, left } = computeFlyoutPosition(
      anchor,
      { width: panel.width, height: panel.height },
      { width: window.innerWidth, height: window.innerHeight },
    );

    this.renderer.setStyle(flyout, 'top', `${top}px`);
    this.renderer.setStyle(flyout, 'left', `${left}px`);
    this.renderer.setStyle(flyout, 'right', 'auto');
    this.renderer.setStyle(flyout, 'margin', '0');
    // Above the scrolling dropdown (z-index 1000 in app.css) so it is not
    // painted underneath the panel it just escaped.
    this.renderer.setStyle(flyout, 'zIndex', '1001');
    // A panel taller than the viewport scrolls on its own rather than spilling.
    this.renderer.setStyle(flyout, 'maxHeight', `${window.innerHeight - top - 8}px`);
    this.renderer.setStyle(flyout, 'overflowY', 'auto');
  }

  private close(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.unlistenReposition.forEach(fn => fn());
    this.unlistenReposition = [];
    this.restoreParentScrollbar();

    const flyout = this.flyout;
    if (!flyout) {
      return;
    }

    // Hand every property back to the stylesheet rather than freezing a value.
    ['transition', 'position', 'top', 'left', 'right', 'margin', 'zIndex', 'maxHeight', 'overflowY']
      .forEach(prop => this.renderer.removeStyle(flyout, prop));
  }
}
